import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";

const SESSION_KEY = "tp_anon_save_prompt_seen";

export const SaveDataInterstitial = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => {
      if (!user?.anonymous) return;
      if (sessionStorage.getItem(SESSION_KEY)) return;
      setOpen(true);
    };
    window.addEventListener("taperpath:anon-data-saved", handler);
    return () => window.removeEventListener("taperpath:anon-data-saved", handler);
  }, [user]);

  const dismiss = (goSave) => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(false);
    if (goSave) navigate("/app/settings");
  };

  if (!user?.anonymous) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(false); }}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="anon-save-interstitial">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-[#E8907E]/15 text-[#D96C5B]">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <DialogTitle className="font-head text-xl">Your progress isn't saved yet</DialogTitle>
          <DialogDescription className="text-[#2C363F]">
            You're browsing anonymously. Everything you log — your taper data, mood, sleep and journal history — lives only in this session and <strong>will be permanently lost</strong> if you close the tab or your session ends.
            <br /><br />
            Create a free account with an email and password to keep it all safe and log back in on any device. Your data stays private.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button data-testid="interstitial-save-btn" onClick={() => dismiss(true)} className="w-full rounded-full bg-[#4A7c59] py-6 text-base font-semibold hover:bg-[#3B6347]">
            Save my account <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button data-testid="interstitial-dismiss-btn" variant="ghost" onClick={() => dismiss(false)} className="w-full rounded-full">
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
